import { IconInfo } from 'components/Icons/InfoIcon'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { useIsMobile } from 'utils/useIsMobile'

interface ApyTooltipProps {
  strategyYieldRatio24h?: {
    [x: string]: number
  }
  apy?: number
}

export const ApyTooltip = ({ apy, strategyYieldRatio24h }: ApyTooltipProps) => {
  if (typeof apy === 'undefined') {
    return null
  }

  const kaiLeverageSupplyPool = strategyYieldRatio24h?.['kaiLeverageSupplyPool']
  const incentives = strategyYieldRatio24h?.['incentives']
  const kaiLeverageSupplyPoolValue =
    typeof kaiLeverageSupplyPool !== 'undefined' ? apy * 100 * kaiLeverageSupplyPool : undefined
  const incentivesValue = typeof incentives !== 'undefined' ? apy * 100 * incentives : undefined

  return (
    <Tooltip
      content={
        <div>
          <div style={{ borderBottom: '1px solid', paddingBottom: 8 }}>
            Annual percentage yield after performance fees. Consists of collected interest and liquidity incentives.
          </div>
          {typeof kaiLeverageSupplyPoolValue === 'number' && (
            <div style={{ padding: 4, paddingTop: 8, display: 'flex', justifyContent: 'space-between' }}>
              <div>Interest rates</div>
              <div>{kaiLeverageSupplyPoolValue.toFixed(2) + '%'}</div>
            </div>
          )}
          {typeof incentivesValue === 'number' && (
            <div style={{ padding: 4, display: 'flex', justifyContent: 'space-between' }}>
              <div>Liquidity incentives</div>
              <div>{incentivesValue.toFixed(2) + '%'}</div>
            </div>
          )}
          <div style={{ padding: 4, display: 'flex', justifyContent: 'space-between', fontWeight: 700 }}>
            <div>Total APY</div>
            <div>{(apy * 100).toFixed(2) + '%'}</div>
          </div>
        </div>
      }
      position="top"
    >
      <IconInfo width={16} height={16} style={{ marginLeft: 6 }} />
    </Tooltip>
  )
}

interface BalanceTooltipProps {
  equity?: string
  deposited?: string
  unrealizedProfit?: string
}

export const BalanceTooltip = ({ equity, deposited, unrealizedProfit }: BalanceTooltipProps) => {
  if (!equity || !deposited || !unrealizedProfit) {
    return null
  }

  return (
    <Tooltip
      content={
        <div>
          <div style={{ borderBottom: '1px solid', paddingBottom: 8 }}>
            Your current vault balance. Consists of your deposits and unrealized profit still in the vault.
          </div>
          <div style={{ padding: 4, paddingTop: 8, display: 'flex', justifyContent: 'space-between' }}>
            <div>Your deposits</div>
            <div>{deposited}</div>
          </div>
          <div style={{ padding: 4, display: 'flex', justifyContent: 'space-between' }}>
            <div>Unrealized profit</div>
            <div>{unrealizedProfit}</div>
          </div>
          <div style={{ padding: 4, display: 'flex', justifyContent: 'space-between', fontWeight: 700 }}>
            <div>Total balance</div>
            <div>{equity}</div>
          </div>
        </div>
      }
      position="top"
    >
      <IconInfo width={16} height={16} style={{ marginLeft: 6 }} />
    </Tooltip>
  )
}

interface EarnedTooltipProps {
  realizedProfit?: string
  unrealizedProfit?: string
  earned?: string
}

export const EarnedTooltip = ({ realizedProfit, unrealizedProfit, earned }: EarnedTooltipProps) => {
  const isMobile = useIsMobile()

  if (!realizedProfit || !unrealizedProfit || !earned) {
    return null
  }

  return (
    <Tooltip
      content={
        <div>
          <div style={{ borderBottom: '1px solid', paddingBottom: 8 }}>
            Total profit you’ve made so far. Includes unrealized (still in the vault) and realized profit (withdrawn).
          </div>
          <div style={{ padding: 4, paddingTop: 8, display: 'flex', justifyContent: 'space-between' }}>
            <div>Realized profit</div>
            <div>{realizedProfit}</div>
          </div>
          <div style={{ padding: 4, display: 'flex', justifyContent: 'space-between' }}>
            <div>Unrealized profit</div>
            <div>{unrealizedProfit}</div>
          </div>
          <div style={{ padding: 4, display: 'flex', justifyContent: 'space-between', fontWeight: 700 }}>
            <div>Total earned</div>
            <div>{earned}</div>
          </div>
        </div>
      }
      position="top"
      tooltipBoxStyles={isMobile ? { marginLeft: 50 } : {}}
    >
      <IconInfo width={16} height={16} style={{ marginLeft: 6 }} />
    </Tooltip>
  )
}
